import { useState, useRef, useCallback, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import type { EventInput, EventClickArg, DateSelectArg, EventDropArg } from '@fullcalendar/core'
import { calendarApi, type CalendarTask, type CreateCalendarTaskDto, type LinkableResult, type TaskRefType } from '../api/calendar'
import { usersApi, type User } from '../api/settings'
import { Modal } from '../components/ui/Modal'
import { EventDetailModal } from '../components/calendar/EventDetailModal'
import { taskColor, formatTaskDateTime as formatDateTime } from '../lib/taskColors'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { DropdownSelect, type DropdownOption } from '../components/ui/DropdownSelect'
import { useAuthStore } from '../store/auth'
import { cn, fullName } from '../lib/utils'
import { Plus, Clock, Link2, Edit2, Trash2, X, ChevronLeft, ChevronRight, Maximize2, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import { useForm, Controller } from 'react-hook-form'

// Color constants


// ─── Create / Edit Modal ─────────────────────────────────────────────────────

// Generate 24h time slots in HH:MM format, step = minutes
function timeSlots(stepMin: number): string[] {
  const slots: string[] = []
  for (let m = 0; m < 24 * 60; m += stepMin) {
    const h = String(Math.floor(m / 60)).padStart(2, '0')
    const min = String(m % 60).padStart(2, '0')
    slots.push(`${h}:${min}`)
  }
  return slots
}

// Generate end-time options starting at startTime + 15min, up to +8h, with duration label
function endTimeOptions(date: string, startTime: string): DropdownOption[] {
  if (!date || !startTime) return timeSlots(15).map(t => ({ value: `${date}T${t}`, label: t }))
  const [sh, sm] = startTime.split(':').map(Number)
  const startTotal = sh * 60 + sm
  const opts: DropdownOption[] = []
  for (let add = 15; add <= 8 * 60; add += 15) {
    const total = startTotal + add
    if (total >= 24 * 60) break
    const h = String(Math.floor(total / 60)).padStart(2, '0')
    const min = String(total % 60).padStart(2, '0')
    const durationH = add / 60
    const label = durationH < 1
      ? `${h}:${min} (${add} min)`
      : `${h}:${min} (${durationH % 1 === 0 ? durationH : durationH.toFixed(1)} h)`
    opts.push({ value: `${date}T${h}:${min}`, label })
  }
  return opts
}

function isoToDateAndTime(iso: string): { date: string; time: string } {
  if (!iso) return { date: '', time: '' }
  const d = new Date(iso)
  const date = d.toISOString().slice(0, 10)
  const h = String(d.getHours()).padStart(2, '0')
  const m = String(d.getMinutes()).padStart(2, '0')
  return { date, time: `${h}:${m}` }
}

interface TaskFormValues {
  title: string
  type: 'meeting' | 'reminder'
  isAllDay: boolean
  dueDate: string
  startDate: string
  startTime: string
  endAt: string
  description: string
  assignedTo: string
  refType: TaskRefType | ''
  refId: string
}

interface CreateTaskModalProps {
  open: boolean
  onClose: () => void
  companyId: string
  users: User[]
  isOwner: boolean
  prefill?: { start?: string; end?: string; allDay?: boolean }
  editing?: CalendarTask | null
}

function CreateTaskModal({ open, onClose, companyId, users, isOwner, prefill, editing }: CreateTaskModalProps) {
  const qc = useQueryClient()
  const [linkSearch, setLinkSearch] = useState('')
  const [linkResults, setLinkResults] = useState<LinkableResult[]>([])
  const [linkLoading, setLinkLoading] = useState(false)
  const [selectedLink, setSelectedLink] = useState<LinkableResult | null>(null)
  const linkTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { register, handleSubmit, watch, setValue, reset, control, formState: { errors } } = useForm<TaskFormValues>({
    defaultValues: {
      title: '', type: 'reminder', isAllDay: true,
      dueDate: '', startDate: '', startTime: '09:00', endAt: '',
      description: '', assignedTo: '', refType: '', refId: '',
    },
  })

  const isAllDay = watch('isAllDay')
  const taskType = watch('type')
  const startDate = watch('startDate')
  const startTime = watch('startTime')

  // Reset form when modal opens / prefill changes
  useEffect(() => {
    if (!open) return
    if (editing) {
      const st = editing.startAt ? isoToDateAndTime(editing.startAt) : { date: '', time: '09:00' }
      reset({
        title: editing.title,
        type: editing.type,
        isAllDay: editing.isAllDay,
        dueDate: editing.dueDate ? editing.dueDate.slice(0, 10) : '',
        startDate: st.date,
        startTime: st.time,
        endAt: editing.endAt ? `${st.date}T${isoToDateAndTime(editing.endAt).time}` : '',
        description: editing.description ?? '',
        assignedTo: editing.assignedTo ?? '',
        refType: (editing.refType as TaskRefType | '') ?? '',
        refId: editing.refId ?? '',
      })
      if (editing.linkedRecordName && editing.refType && editing.refId) {
        setSelectedLink({ id: editing.refId, type: editing.refType as TaskRefType, label: editing.linkedRecordName })
      } else {
        setSelectedLink(null)
      }
    } else {
      const prefillDate = prefill?.start ? prefill.start.slice(0, 10) : ''
      const prefillSt = prefill?.start && !prefill.allDay ? isoToDateAndTime(prefill.start) : { date: prefillDate, time: '09:00' }
      const prefillEnd = prefill?.end && !prefill.allDay ? `${prefillDate}T${isoToDateAndTime(prefill.end).time}` : ''
      reset({
        title: '', type: 'reminder', isAllDay: prefill?.allDay ?? true,
        dueDate: prefillDate,
        startDate: prefillSt.date,
        startTime: prefillSt.time,
        endAt: prefillEnd,
        description: '', assignedTo: '', refType: '', refId: '',
      })
      setSelectedLink(null)
    }
    setLinkSearch('')
    setLinkResults([])
  }, [open, editing, prefill, reset])

  const createMut = useMutation({
    mutationFn: (dto: CreateCalendarTaskDto) => calendarApi.create(companyId, dto),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['calendar', companyId] }); toast.success('Task created'); onClose() },
    onError: () => toast.error('Failed to create task'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Partial<CreateCalendarTaskDto> }) =>
      calendarApi.update(companyId, id, dto),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['calendar', companyId] }); toast.success('Task updated'); onClose() },
    onError: () => toast.error('Failed to update task'),
  })

  const onSubmit = (values: TaskFormValues) => {
    const startIso = !values.isAllDay && values.startDate && values.startTime
      ? new Date(`${values.startDate}T${values.startTime}:00`).toISOString()
      : undefined
    const endIso = !values.isAllDay && values.endAt
      ? new Date(`${values.endAt}:00`).toISOString()
      : undefined
    const dto: CreateCalendarTaskDto = {
      title: values.title,
      type: values.type,
      isAllDay: values.isAllDay,
      dueDate: values.isAllDay ? (values.dueDate || undefined) : undefined,
      startAt: startIso,
      endAt: endIso,
      description: values.description || undefined,
      assignedTo: values.assignedTo || undefined,
      refType: selectedLink?.type ?? undefined,
      refId: selectedLink?.id ?? undefined,
    }
    if (editing) {
      updateMut.mutate({ id: editing.id, dto })
    } else {
      createMut.mutate(dto)
    }
  }

  // Link search debounce
  const handleLinkSearch = (q: string) => {
    setLinkSearch(q)
    if (linkTimer.current) clearTimeout(linkTimer.current)
    if (!q.trim()) { setLinkResults([]); return }
    linkTimer.current = setTimeout(async () => {
      setLinkLoading(true)
      try {
        const res = await calendarApi.searchLinkable(companyId, { q: q.trim() })
        setLinkResults(res.data)
      } finally {
        setLinkLoading(false)
      }
    }, 300)
  }

  const userOptions: DropdownOption[] = [
    { value: '', label: 'Unassigned' },
    ...users.map(u => ({ value: u.id, label: fullName(u.firstName, u.lastName) })),
  ]

  const isPending = createMut.isPending || updateMut.isPending

  return (
    <Modal open={open} onClose={onClose} title={editing ? 'Edit Task' : 'New Task'} size="lg">
      <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
        {/* Type selector */}
        <div className="flex gap-2">
          {(['meeting', 'reminder'] as const).map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setValue('type', t)}
              className={cn(
                'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
                taskType === t
                  ? t === 'meeting'
                    ? 'bg-indigo-600 border-indigo-600 text-white'
                    : 'bg-amber-500 border-amber-500 text-white'
                  : 'border-gray-300 text-gray-600 hover:border-gray-400',
              )}
            >
              {t === 'meeting' ? '📅 Meeting' : '🔔 Reminder'}
            </button>
          ))}
        </div>

        {/* Title */}
        <div>
          <label className="form-label">Title <span className="text-red-500">*</span></label>
          <input
            {...register('title', { required: true })}
            className={cn('input w-full', errors.title && 'border-red-500 focus:ring-red-500')}
            placeholder="Task title"
          />
        </div>

        {/* All-day toggle */}
        <div className="flex items-center gap-3">
          <Controller
            name="isAllDay"
            control={control}
            render={({ field }) => (
              <button
                type="button"
                onClick={() => field.onChange(!field.value)}
                className={cn(
                  'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200',
                  field.value ? 'bg-primary-600' : 'bg-gray-300',
                )}
              >
                <span
                  className={cn(
                    'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200',
                    field.value ? 'translate-x-4' : 'translate-x-0',
                  )}
                />
              </button>
            )}
          />
          <span className="text-sm text-gray-700">All day</span>
        </div>

        {/* Date / time fields */}
        {isAllDay ? (
          <div>
            <label className="form-label">Date</label>
            <input type="date" {...register('dueDate')} className="input w-full" />
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="form-label">Date</label>
              <input type="date" {...register('startDate')} className="input w-full" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="form-label">Start time</label>
                <Controller
                  name="startTime"
                  control={control}
                  render={({ field }) => (
                    <DropdownSelect
                      options={timeSlots(30).map(t => ({ value: t, label: t }))}
                      value={field.value}
                      onChange={v => {
                        field.onChange(v)
                        setValue('endAt', '')
                      }}
                      placeholder="--:--"
                      searchable
                    />
                  )}
                />
              </div>
              <div>
                <label className="form-label">End time</label>
                <Controller
                  name="endAt"
                  control={control}
                  render={({ field }) => (
                    <DropdownSelect
                      options={endTimeOptions(startDate, startTime)}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="--:--"
                      searchable
                    />
                  )}
                />
              </div>
            </div>
          </div>
        )}

        {/* Assigned to (owner only) */}
        {isOwner && (
          <div>
            <label className="form-label">Assign to</label>
            <Controller
              name="assignedTo"
              control={control}
              render={({ field }) => (
                <DropdownSelect
                  options={userOptions}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Unassigned"
                />
              )}
            />
          </div>
        )}

        {/* Linked record */}
        <div>
          <label className="form-label">Link to lead / deal / contact</label>
          {selectedLink ? (
            <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
              <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-800 flex-1">{selectedLink.label}</span>
              <span className="text-xs text-gray-400 capitalize">{selectedLink.type}</span>
              <button type="button" onClick={() => setSelectedLink(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                value={linkSearch}
                onChange={e => handleLinkSearch(e.target.value)}
                className="input w-full"
                placeholder="Search leads, deals, contacts..."
              />
              {(linkResults.length > 0 || linkLoading) && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                  {linkLoading && <div className="px-3 py-2 text-sm text-gray-400">Searching...</div>}
                  {linkResults.map(r => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => { setSelectedLink(r); setLinkSearch(''); setLinkResults([]) }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <span className="text-xs text-gray-400 capitalize w-12 flex-shrink-0">{r.type}</span>
                      <span className="text-sm text-gray-800 truncate">{r.label}</span>
                      {r.sub && <span className="text-xs text-gray-400 ml-auto flex-shrink-0 truncate max-w-[120px]">{r.sub}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="form-label">Description</label>
          <textarea {...register('description')} rows={3} className="input w-full resize-none" placeholder="Optional notes..." />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button type="submit" disabled={isPending} className="btn btn-primary">
            {isPending ? 'Saving...' : editing ? 'Save changes' : 'Create task'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ─── Event Detail Popup ───────────────────────────────────────────────────────

interface DetailPopupProps {
  task: CalendarTask
  anchor: { x: number; y: number }
  companyId: string
  isOwner: boolean
  onClose: () => void
  onExpand: () => void
  onEdit: () => void
  onDelete: () => void
}

function DetailPopup({ task, anchor, companyId, isOwner, onClose, onExpand, onEdit, onDelete }: DetailPopupProps) {
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  const color = taskColor(task.type)
  const timeLabel = task.isAllDay
    ? (task.dueDate ? new Date(task.dueDate).toLocaleDateString([], { dateStyle: 'medium' }) : 'All day')
    : `${formatDateTime(task.startAt)} – ${formatDateTime(task.endAt)}`

  const createdAtLabel = new Date(task.createdAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })

  const linkedHref = task.refType && task.refId
    ? `/company/${companyId}/${task.refType === 'contact' ? 'contacts' : task.refType + 's'}/${task.refId}`
    : null

  const style: React.CSSProperties = {
    position: 'fixed',
    top: Math.min(anchor.y, window.innerHeight - 340),
    left: Math.min(anchor.x, window.innerWidth - 280),
    zIndex: 200,
  }

  return (
    <div ref={ref} style={{ ...style, width: 272 }} className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3" style={{ borderLeft: `4px solid ${color}` }}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <span
              className="inline-block text-xs font-medium px-2 py-0.5 rounded-full text-white mb-1"
              style={{ backgroundColor: color }}
            >
              {task.type === 'meeting' ? 'Meeting' : 'Reminder'}
            </span>
            <p className="font-semibold text-gray-900 text-sm leading-snug">{task.title}</p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
            <button onClick={onExpand} title="View details" className="text-gray-400 hover:text-gray-600">
              <Maximize2 className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="mt-2 space-y-1.5 text-xs text-gray-500">
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{timeLabel}</span>
          </div>
          {task.assignedUser && (
            <div className="flex items-center gap-1.5">
              <div className="w-3.5 h-3.5 rounded-full bg-primary-100 flex items-center justify-center text-[9px] font-semibold text-primary-700 flex-shrink-0">
                {task.assignedUser.firstName[0]}
              </div>
              <span>Assigned: {fullName(task.assignedUser.firstName, task.assignedUser.lastName)}</span>
            </div>
          )}
          {linkedHref && task.linkedRecordName && (
            <div className="flex items-center gap-1.5">
              <Link2 className="w-3.5 h-3.5 flex-shrink-0" />
              <button
                onClick={() => { onClose(); navigate(linkedHref) }}
                className="truncate text-primary-600 hover:underline text-left"
              >
                <span className="capitalize text-gray-400 mr-1">{task.refType}:</span>
                {task.linkedRecordName}
              </button>
            </div>
          )}
          {task.description && (
            <p className="text-gray-600 mt-1 line-clamp-3">{task.description}</p>
          )}
          <div className="pt-1 border-t border-gray-100 space-y-1">
            <div className="flex items-center gap-1.5 text-gray-400">
              <Clock className="w-3 h-3 flex-shrink-0" />
              <span>Created {createdAtLabel}</span>
            </div>
            {isOwner && task.createdByUser && (
              <div className="flex items-center gap-1.5 text-gray-400">
                <div className="w-3 h-3 rounded-full bg-gray-200 flex-shrink-0" />
                <span>By {fullName(task.createdByUser.firstName, task.createdByUser.lastName)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex border-t border-gray-100">
        <button
          onClick={onEdit}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <Edit2 className="w-3.5 h-3.5" />
          Edit
        </button>
        <div className="w-px bg-gray-100" />
        <button
          onClick={onDelete}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs text-red-500 hover:bg-red-50 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Delete
        </button>
      </div>
    </div>
  )
}

// ─── Event Detail Modal (full, read-only) ─────────────────────────────────────

// ─── Drag Confirm Dialog ──────────────────────────────────────────────────────

interface DragConfirmProps {
  open: boolean
  task: CalendarTask | null
  newDate: string
  onConfirm: () => void
  onCancel: () => void
}

function DragConfirmDialog({ open, task, newDate, onConfirm, onCancel }: DragConfirmProps) {
  if (!open || !task) return null
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onCancel} />
      <div className="relative bg-white rounded-xl shadow-xl p-5 max-w-sm w-full">
        <p className="font-semibold text-gray-900 mb-1">Move task?</p>
        <p className="text-sm text-gray-500 mb-4">
          Move "<span className="font-medium text-gray-700">{task.title}</span>" to{' '}
          <span className="font-medium text-gray-700">{newDate}</span>?
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="btn btn-secondary btn-sm">Cancel</button>
          <button onClick={onConfirm} className="btn btn-primary btn-sm">Move</button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Calendar Page ───────────────────────────────────────────────────────

export function CalendarPage() {
  usePageTitle('Calendar')
  const { companyId } = useParams<{ companyId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuthStore()
  const qc = useQueryClient()
  const calRef = useRef<FullCalendar>(null)
  const isOwner = user?.role === 'company_owner' || user?.role === 'administrator'

  const [view, setView] = useState<'dayGridMonth' | 'timeGridWeek'>('timeGridWeek')
  const [selectedUserId, setSelectedUserId] = useState('')
  const [currentTitle, setCurrentTitle] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [prefill, setPrefill] = useState<{ start?: string; end?: string; allDay?: boolean } | undefined>()
  const [editingTask, setEditingTask] = useState<CalendarTask | null>(null)
  const [detail, setDetail] = useState<{ task: CalendarTask; pos: { x: number; y: number } } | null>(null)
  const [detailModalTask, setDetailModalTask] = useState<CalendarTask | null>(null)
  const [dragConfirm, setDragConfirm] = useState<{
    task: CalendarTask; newStart: string; newEnd: string | null; allDay: boolean; revert: () => void
  } | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [searchResults, setSearchResults] = useState<CalendarTask[]>([])
  const [searchOpen, setSearchOpen] = useState(false)
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Date range for query: track via FullCalendar's datesSet callback
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date()
    const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7).toISOString()
    const to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 60).toISOString()
    return { from, to }
  })

  const { data: users = [] } = useQuery({
    queryKey: ['users', companyId],
    queryFn: () => usersApi.list(companyId!).then(r => r.data),
    enabled: !!companyId && isOwner,
    staleTime: 5 * 60 * 1000,
  })

  const { data: tasks = [] } = useQuery({
    queryKey: ['calendar', companyId, dateRange.from, dateRange.to, selectedUserId],
    queryFn: () => calendarApi.list(companyId!, {
      from: dateRange.from,
      to: dateRange.to,
      userId: selectedUserId || undefined,
    }).then(r => r.data),
    enabled: !!companyId,
  })

  // Deep link from a notification: /calendar?event=<taskId> — fetch the event,
  // jump to its date, open the detail modal, then clear the param.
  useEffect(() => {
    const eventId = searchParams.get('event')
    if (!eventId || !companyId) return
    let cancelled = false
    calendarApi.getOne(companyId, eventId)
      .then(r => {
        if (cancelled) return
        const task = r.data
        const dateStr = task.startAt ?? task.dueDate ?? task.createdAt
        calRef.current?.getApi().gotoDate(new Date(dateStr))
        setDetailModalTask(task)
      })
      .catch(() => { /* event may have been deleted — ignore */ })
      .finally(() => {
        if (cancelled) return
        searchParams.delete('event')
        setSearchParams(searchParams, { replace: true })
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, companyId])

  const deleteMut = useMutation({
    mutationFn: (taskId: string) => calendarApi.remove(companyId!, taskId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['calendar', companyId] }); toast.success('Task deleted') },
    onError: () => toast.error('Failed to delete task'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Parameters<typeof calendarApi.update>[2] }) =>
      calendarApi.update(companyId!, id, dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calendar', companyId] }),
    onError: () => toast.error('Failed to move task'),
  })

  // Map tasks → FullCalendar events
  const events: EventInput[] = tasks.map(t => {
    const color = taskColor(t.type)
    const base: EventInput = {
      id: t.id,
      title: t.title,
      backgroundColor: color,
      borderColor: color,
      extendedProps: { task: t },
    }
    if (t.isAllDay || (!t.startAt && !t.endAt)) {
      return { ...base, date: t.dueDate ?? t.createdAt.slice(0, 10), allDay: true }
    }
    return {
      ...base,
      start: t.startAt!,
      end: t.endAt ?? undefined,
      allDay: false,
    }
  })

  const handleEventClick = useCallback((arg: EventClickArg) => {
    const task = arg.event.extendedProps.task as CalendarTask
    const rect = arg.el.getBoundingClientRect()
    setDetail({ task, pos: { x: rect.right + 8, y: rect.top } })
  }, [])

  const handleDateSelect = useCallback((arg: DateSelectArg) => {
    setPrefill({ start: arg.startStr, end: arg.endStr, allDay: arg.allDay })
    setEditingTask(null)
    setShowCreate(true)
  }, [])

  const handleEventDrop = useCallback((arg: EventDropArg) => {
    const task = arg.event.extendedProps.task as CalendarTask
    const newStart = arg.event.startStr
    const newEnd = arg.event.endStr || null
    const allDay = arg.event.allDay
    setDragConfirm({ task, newStart, newEnd, allDay, revert: arg.revert })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const confirmDrag = () => {
    if (!dragConfirm) return
    const { task, newStart, newEnd, allDay } = dragConfirm
    const dto = allDay
      ? { isAllDay: true, dueDate: newStart.slice(0, 10), startAt: undefined, endAt: undefined }
      : { isAllDay: false, startAt: newStart, endAt: newEnd ?? undefined, dueDate: undefined }
    updateMut.mutate({ id: task.id, dto })
    setDragConfirm(null)
  }

  const cancelDrag = () => {
    dragConfirm?.revert()
    setDragConfirm(null)
  }

  const handleDeleteTask = (taskId: string) => {
    setDetail(null)
    setDeleteConfirm(taskId)
  }

  const handleEditTask = (task: CalendarTask) => {
    setDetail(null)
    setEditingTask(task)
    setShowCreate(true)
  }

  const goPrev = () => calRef.current?.getApi().prev()
  const goNext = () => calRef.current?.getApi().next()
  const goToday = () => calRef.current?.getApi().today()

  const switchView = (v: typeof view) => {
    setView(v)
    calRef.current?.getApi().changeView(v)
  }

  const handleSearch = (q: string) => {
    setSearch(q)
    if (searchTimer.current) clearTimeout(searchTimer.current)
    if (!q.trim()) { setSearchResults([]); setSearchOpen(false); return }
    searchTimer.current = setTimeout(async () => {
      const res = await calendarApi.searchEvents(companyId!, q.trim())
      setSearchResults(res.data)
      setSearchOpen(true)
    }, 300)
  }

  const openSearchResult = (task: CalendarTask) => {
    const dateStr = task.startAt ?? task.dueDate ?? task.createdAt
    calRef.current?.getApi().gotoDate(new Date(dateStr))
    setSearch(''); setSearchResults([]); setSearchOpen(false)
    setDetailModalTask(task)
  }

  useEffect(() => {
    if (!searchOpen) return
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.calendar-search-box')) setSearchOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [searchOpen])

  const userOptions: DropdownOption[] = [
    { value: '', label: 'All Users' },
    ...users.map(u => ({ value: u.id, label: fullName(u.firstName, u.lastName) })),
  ]

  return (
    <div className="flex flex-col h-full min-h-0 bg-white">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-3 border-b border-gray-200 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 mr-2">
          <button onClick={goPrev} className="btn-ghost btn-sm p-1.5 rounded-lg">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={goNext} className="btn-ghost btn-sm p-1.5 rounded-lg">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button onClick={goToday} className="btn btn-secondary btn-sm px-3 py-1.5 text-xs">Today</button>
          <span className="text-sm font-semibold text-gray-900 ml-1 whitespace-nowrap">{currentTitle}</span>
        </div>

        <div className="calendar-search-box relative w-60">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            value={search}
            onChange={e => handleSearch(e.target.value)}
            onFocus={() => { if (searchResults.length) setSearchOpen(true) }}
            placeholder="Search events..."
            className="input w-full pl-8"
          />
          {searchOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[120] max-h-72 overflow-y-auto">
              {searchResults.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-400">No matching events</div>
              ) : searchResults.map(t => {
                const dateLabel = t.isAllDay
                  ? (t.dueDate ? new Date(t.dueDate).toLocaleDateString([], { dateStyle: 'medium' }) : '')
                  : formatDateTime(t.startAt)
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => openSearchResult(t)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: taskColor(t.type) }} />
                    <span className="text-sm text-gray-800 truncate flex-1">{t.title}</span>
                    {t.linkedRecordName && <span className="text-xs text-gray-400 truncate max-w-[100px]">{t.linkedRecordName}</span>}
                    <span className="text-xs text-gray-400 flex-shrink-0">{dateLabel}</span>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <div className="flex-1" />

        {isOwner && (
          <div className="w-44">
            <DropdownSelect
              options={userOptions}
              value={selectedUserId}
              onChange={setSelectedUserId}
              placeholder="All Users"
            />
          </div>
        )}

        <div className="flex border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={() => switchView('timeGridWeek')}
            className={cn('px-3 py-1.5 text-xs font-medium transition-colors', view === 'timeGridWeek' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50')}
          >
            Week
          </button>
          <button
            onClick={() => switchView('dayGridMonth')}
            className={cn('px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-200', view === 'dayGridMonth' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50')}
          >
            Month
          </button>
        </div>

        <button
          onClick={() => { setEditingTask(null); setPrefill(undefined); setShowCreate(true) }}
          className="btn btn-primary btn-sm flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" />
          New task
        </button>
      </div>

      {/* Calendar */}
      <div className="flex-1 min-h-0 px-4 py-3 overflow-auto calendar-container">
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={view}
          headerToolbar={false}
          height="100%"
          events={events}
          editable
          selectable
          selectMirror
          dayMaxEvents={4}
          weekends
          nowIndicator
          allDaySlot
          slotMinTime="06:00:00"
          slotMaxTime="22:00:00"
          slotDuration="00:30:00"
          eventClick={handleEventClick}
          select={handleDateSelect}
          eventDrop={handleEventDrop}
          datesSet={info => {
            setCurrentTitle(info.view.title)
            setDateRange({ from: info.startStr, to: info.endStr })
          }}
          eventContent={renderEventContent}
          dayCellClassNames={arg => {
            const day = arg.date.getDay()
            return day === 0 || day === 6 ? ['fc-weekend-cell'] : []
          }}
        />
      </div>

      {/* Create / Edit modal */}
      <CreateTaskModal
        open={showCreate}
        onClose={() => { setShowCreate(false); setEditingTask(null) }}
        companyId={companyId!}
        users={users}
        isOwner={isOwner}
        prefill={prefill}
        editing={editingTask}
      />

      {/* Detail popup */}
      {detail && (
        <DetailPopup
          task={detail.task}
          anchor={detail.pos}
          companyId={companyId!}
          isOwner={isOwner}
          onClose={() => setDetail(null)}
          onExpand={() => { setDetailModalTask(detail.task); setDetail(null) }}
          onEdit={() => handleEditTask(detail.task)}
          onDelete={() => handleDeleteTask(detail.task.id)}
        />
      )}

      {/* Event detail modal */}
      <EventDetailModal
        task={detailModalTask}
        companyId={companyId!}
        isOwner={isOwner}
        onClose={() => setDetailModalTask(null)}
        onEdit={(t) => { setDetailModalTask(null); handleEditTask(t) }}
      />

      {/* Drag confirm */}
      <DragConfirmDialog
        open={!!dragConfirm}
        task={dragConfirm?.task ?? null}
        newDate={dragConfirm ? new Date(dragConfirm.newStart).toLocaleDateString([], { dateStyle: 'medium' }) : ''}
        onConfirm={confirmDrag}
        onCancel={cancelDrag}
      />

      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => { if (deleteConfirm) deleteMut.mutate(deleteConfirm); setDeleteConfirm(null) }}
        title="Delete this task?"
        message="This task will be removed."
        confirmLabel="Delete"
        isPending={deleteMut.isPending}
      />
    </div>
  )
}

function renderEventContent(arg: { event: { title: string; extendedProps: { task: CalendarTask } } }) {
  const task = arg.event.extendedProps.task
  if (!task) return null
  const color = taskColor(task.type)
  return (
    <div className="flex items-center gap-1 px-1 py-0.5 min-w-0">
      <span
        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: color }}
      />
      <span className="text-[11px] font-medium leading-tight truncate">{arg.event.title}</span>
    </div>
  )
}
